import { DateTimeInput, Field, NumericInput, RadioGroup, TextInput, TextOutput } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ListFieldSection } from 'Components/Layout';
import { DateTimeStyle } from 'Components/Layout/Grid/types';
import {
  IWorksheetComponent,
  WorksheetBalanceFieldSet,
  WorksheetEquipmentDropdown,
  WorksheetGridFieldSection,
  WorksheetRecordGrid,
} from 'Components/Worksheets/Core';
import { createDryMassEntryOptionSelector } from 'Components/Worksheets/MoistureContentWorksheet';
import { getDryMassGridFieldSet } from 'Components/Worksheets/MoistureContentWorksheet/As/1289_2_1_1/gridFieldSets';
import { DryingEquipment } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/types';
import { pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';

let SeparationOvenDryingSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const dryingEquipment = useSelector(
    createWorksheetSelector!(
      'Separation.FinePortion.Subspecimen.Masses.MassMeasurement.MeasuredConstantDryMass.Equipment.DryingEquipment'
    )
  ) as DryingEquipment;

  const dryMassEntryOption = useSelector(createDryMassEntryOptionSelector(createWorksheetSelector!));

  const showTimeInOven = useSelector(
    pipe(createDryMassEntryOptionSelector(createWorksheetSelector!), (v) => v !== 'Never')
  );

  const showSingleReadingSection = useSelector(
    pipe(
      createWorksheetSelector!(
        'Separation.FinePortion.Subspecimen.Masses.MassMeasurement.MeasuredConstantDryMass.DryingTray.DryMassReadings'
      ),
      (r) => (r?.length ?? 0) <= 1 && dryMassEntryOption === 'Never'
    )
  );

  return (
    <>
      <ListFieldSection heading="Coarse Portion Mass">
        <Field input={<TextOutput />} name="Masses.DryMass" label="Specimen Total Dry Mass" />
        <Field input={<TextOutput />} name="Separation.CoarsePortion.Masses.DryMass" />
        <Field input={<TextOutput />} name="Separation.FinePortion.Masses.WetMass" />
        <Field input={<TextOutput />} name="Separation.FinePortion.Masses.DryMass" />
      </ListFieldSection>
      <NamePrefixModifier appendNamePrefix="Separation.FinePortion.Subspecimen.Masses">
        <ListFieldSection>
          <Field input={<TextInput />} name="MassMeasurement.TareID" />
          <Field input={<NumericInput />} name="MassMeasurement.TareMass" />
          <Field input={<NumericInput />} name="MassMeasurement.TareAndWetMass" />
          <Field input={<TextOutput />} name="MassMeasurement.WetMass" />
        </ListFieldSection>

        <NamePrefixModifier appendNamePrefix="MassMeasurement.MeasuredConstantDryMass">
          <ListFieldSection heading="Sieve Portion Dry Mass">
            <Field
              input={<RadioGroup />}
              name="Equipment.DryingEquipment"
              options={[
                { label: 'Oven', value: DryingEquipment.Oven },
                { label: 'Microwave Oven', value: DryingEquipment.MicrowaveOven },
                { label: 'Infrared Light', value: DryingEquipment.InfraredLight },
                { label: 'Hotplate', value: DryingEquipment.Hotplate },
              ]}
            />
            <Field
              input={<WorksheetEquipmentDropdown />}
              name="Equipment.Oven"
              equipmentType="oven"
              visible={dryingEquipment === DryingEquipment.Oven}
            />
            <Field
              input={<WorksheetEquipmentDropdown />}
              name="Equipment.MicrowaveOven"
              equipmentType="microwave-oven"
              visible={dryingEquipment === DryingEquipment.MicrowaveOven}
            />
            <Field
              input={<WorksheetEquipmentDropdown />}
              name="Equipment.InfraredLight"
              equipmentType="infrared-light"
              visible={dryingEquipment === DryingEquipment.InfraredLight}
            />
            <Field
              input={<WorksheetEquipmentDropdown />}
              name="Equipment.Hotplate"
              equipmentType="hotplate"
              visible={dryingEquipment === DryingEquipment.Hotplate}
            />
          </ListFieldSection>
          <ListFieldSection>
            <Field
              input={<NumericInput />}
              name="Equipment.DryingTemperature"
              visible={dryingEquipment === DryingEquipment.Oven}
            />
            {showTimeInOven && <Field input={<DateTimeInput />} name="DryingTray.DateTimePlacedInOvenLocal" />}
            <WorksheetBalanceFieldSet name="Equipment.DryMassBalance" />
          </ListFieldSection>
          {showSingleReadingSection ? (
            <ListFieldSection>
              <Field
                input={<NumericInput />}
                name="DryingTray.DryMassReadings[0].DrySoilAndContainerMass"
                label="Tare and Material Dry Mass"
              />
            </ListFieldSection>
          ) : (
            <WorksheetGridFieldSection name="DryingTray.DryMassReadings">
              <WorksheetRecordGrid
                name="DryingTray.DryMassReadings"
                orientation="vertical"
                recordProps={{
                  enforceAtLeastOneRecord: true,
                  preventRemove: 'all-but-last',
                  removeConfirm: {
                    confirm: true,
                    confirmTitle: 'Delete Dry Mass Reading',
                    confirmText: 'Are you sure you want to delete the dry mass reading? Data cannot be recovered.',
                    confirmButtonText: 'Delete',
                  },
                }}
                fieldSet={getDryMassGridFieldSet(dryMassEntryOption, DateTimeStyle.Combined)}
              />
            </WorksheetGridFieldSection>
          )}
        </NamePrefixModifier>
        <ListFieldSection>
          <Field input={<TextOutput />} name="DryMass" />
          <Field
            input={<TextOutput />}
            name="MassMeasurement.MeasuredConstantDryMass.Measurements.TotalDryMassToTotalWetMassRatio"
          />
        </ListFieldSection>
      </NamePrefixModifier>
    </>
  );
};

SeparationOvenDryingSection = withInjectedProps(SeparationOvenDryingSection);

export { SeparationOvenDryingSection };
export default SeparationOvenDryingSection;
